import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Button, DatePicker, Flex, Input, Select, Tag, Typography } from 'antd'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useUnit } from 'effector-react'

import { $countriesList } from '../../../../features/dictionaries/model'
import { transformCountriesToOptions } from '../../../../libs/formatCountries'
import {
  DOCUMENT_TYPE_OPTIONS,
  PRODUCT_OPTIONS,
  STATUS_OPTIONS,
} from './config'

const { RangePicker } = DatePicker
const { Text } = Typography

type Filters = Record<string, any>

type Props = {
  filters: Filters
  setFilters: (values: Partial<Filters>) => void
  resetFilters: () => void
  users: { email: string; fullName?: string }[]
}

const BoolFilter: React.FC<{
  label: string
  value: boolean | undefined
  onChange: (v: boolean | undefined) => void
}> = ({ label, value, onChange }) => (
  <Flex align="center" gap={4}>
    <Text style={{ fontSize: 12, color: '#545c6e' }}>{label}:</Text>
    <Button.Group size="small">
      <Button
        type={value === true ? 'primary' : 'default'}
        onClick={() => onChange(value === true ? undefined : true)}
      >
        Yes
      </Button>
      <Button
        type={value === false ? 'primary' : 'default'}
        onClick={() => onChange(value === false ? undefined : false)}
      >
        No
      </Button>
    </Button.Group>
  </Flex>
)

export const VerificationsListFilters: React.FC<Props> = ({
  filters,
  setFilters,
  resetFilters,
  users,
}) => {
  const countriesList = useUnit($countriesList)

  const [inputRequestId, setInputRequestId] = useState(filters.requestId || '')
  const [inputClientId, setInputClientId] = useState(filters.clientId || '')
  const [inputEmail, setInputEmail] = useState(filters.email || '')
  const [inputFullName, setInputFullName] = useState(filters.fullName || '')

  useEffect(() => {
    setInputRequestId(filters.requestId || '')
  }, [filters.requestId])
  useEffect(() => {
    setInputClientId(filters.clientId || '')
  }, [filters.clientId])
  useEffect(() => {
    setInputEmail(filters.email || '')
  }, [filters.email])
  useEffect(() => {
    setInputFullName(filters.fullName || '')
  }, [filters.fullName])

  const applySearch = (key: string, value: string) => {
    const v = value.trim()
    setFilters({ [key]: v || undefined, page: undefined })
  }

  const dateRange: [Dayjs, Dayjs] | null =
    filters.createdAtFrom && filters.createdAtTo
      ? [dayjs(filters.createdAtFrom), dayjs(filters.createdAtTo)]
      : null

  const countryOptions = useMemo(
    () => [
      { value: 'ALL_ARAB', label: 'Arabic speaking countries' },
      ...transformCountriesToOptions(countriesList).map(({ value, text }) => ({
        value,
        label: text,
      })),
    ],
    [countriesList],
  )

  const managerOptions = useMemo(
    () => users.map((u) => ({ value: u.email, label: u.email })),
    [users],
  )

  const activeTags = useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []
    const push = (key: string, label: string, clear: () => void) =>
      tags.push({ key, label, clear })

    if (filters.requestId)
      push('requestId', `Request: ${filters.requestId}`, () => {
        setInputRequestId('')
        setFilters({ requestId: undefined, page: undefined })
      })
    if (filters.clientId)
      push('clientId', `Client ID: ${filters.clientId}`, () => {
        setInputClientId('')
        setFilters({ clientId: undefined, page: undefined })
      })
    if (filters.email)
      push('email', `Email: ${filters.email}`, () => {
        setInputEmail('')
        setFilters({ email: undefined, page: undefined })
      })
    if (filters.fullName)
      push('fullName', `Name: ${filters.fullName}`, () => {
        setInputFullName('')
        setFilters({ fullName: undefined, page: undefined })
      })
    if (filters.createdAtFrom && filters.createdAtTo)
      push(
        'createdAt',
        `Created: ${filters.createdAtFrom}–${filters.createdAtTo}`,
        () =>
          setFilters({
            createdAtFrom: undefined,
            createdAtTo: undefined,
            page: undefined,
          }),
      )
    if (filters.status?.length)
      push('status', `Status: ${filters.status.join(', ')}`, () =>
        setFilters({ status: undefined, page: undefined }),
      )
    if (filters.documentType?.length)
      push('documentType', `Type: ${filters.documentType.join(', ')}`, () =>
        setFilters({ documentType: undefined, page: undefined }),
      )
    if (filters.products?.length)
      push('products', `Product: ${filters.products.join(', ')}`, () =>
        setFilters({ products: undefined, page: undefined }),
      )
    if (filters.country?.length)
      push('country', `Country: ${filters.country.join(', ')}`, () =>
        setFilters({ country: undefined, page: undefined }),
      )
    if (filters.manager?.length)
      push('manager', `Manager: ${filters.manager.join(', ')}`, () =>
        setFilters({ manager: undefined, page: undefined }),
      )
    if (filters.hasDeposit !== undefined)
      push('hasDeposit', `Deposit: ${filters.hasDeposit ? 'Yes' : 'No'}`, () =>
        setFilters({ hasDeposit: undefined, page: undefined }),
      )
    if (filters.isDuplicate !== undefined)
      push(
        'isDuplicate',
        `Duplicate: ${filters.isDuplicate ? 'Yes' : 'No'}`,
        () => setFilters({ isDuplicate: undefined, page: undefined }),
      )
    if (filters.isPremium !== undefined)
      push('isPremium', `Premium: ${filters.isPremium ? 'Yes' : 'No'}`, () =>
        setFilters({ isPremium: undefined, page: undefined }),
      )
    return tags
  }, [filters, setFilters])

  return (
    <div
      style={{
        borderRadius: 8,
        padding: '12px 14px',
        marginBottom: 12,
        border: '1px solid var(--crm-border, #d9d9d9)',
        background: 'var(--crm-bg, #fff)',
      }}
    >
      <Flex gap={6} wrap="wrap" style={{ marginBottom: 10 }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Request ID"
          value={inputRequestId}
          onChange={(e) => setInputRequestId(e.target.value)}
          onPressEnter={() => applySearch('requestId', inputRequestId)}
          onBlur={() => applySearch('requestId', inputRequestId)}
          allowClear
          onClear={() => {
            setInputRequestId('')
            setFilters({ requestId: undefined, page: undefined })
          }}
          style={{ width: 160 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Client ID"
          value={inputClientId}
          onChange={(e) => setInputClientId(e.target.value.replace(/\D/g, ''))}
          onPressEnter={() => applySearch('clientId', inputClientId)}
          onBlur={() => applySearch('clientId', inputClientId)}
          allowClear
          onClear={() => {
            setInputClientId('')
            setFilters({ clientId: undefined, page: undefined })
          }}
          style={{ width: 120 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Email"
          value={inputEmail}
          onChange={(e) => setInputEmail(e.target.value)}
          onPressEnter={() => applySearch('email', inputEmail)}
          onBlur={() => applySearch('email', inputEmail)}
          allowClear
          onClear={() => {
            setInputEmail('')
            setFilters({ email: undefined, page: undefined })
          }}
          style={{ width: 200 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Full name"
          value={inputFullName}
          onChange={(e) => setInputFullName(e.target.value)}
          onPressEnter={() => applySearch('fullName', inputFullName)}
          onBlur={() => applySearch('fullName', inputFullName)}
          allowClear
          onClear={() => {
            setInputFullName('')
            setFilters({ fullName: undefined, page: undefined })
          }}
          style={{ width: 180 }}
        />
        <RangePicker
          value={dateRange}
          onChange={(vals) => {
            if (vals && vals[0] && vals[1]) {
              setFilters({
                createdAtFrom: vals[0].format('YYYY-MM-DD'),
                createdAtTo: vals[1].format('YYYY-MM-DD'),
                page: undefined,
              })
            } else {
              setFilters({
                createdAtFrom: undefined,
                createdAtTo: undefined,
                page: undefined,
              })
            }
          }}
          format="YYYY-MM-DD"
          placeholder={['Created from', 'to']}
          style={{ width: 260 }}
        />
      </Flex>

      <div
        style={{
          height: 1,
          background: 'var(--crm-border, #e2e5ec)',
          margin: '0 0 10px',
        }}
      />

      <Flex gap={10} wrap="wrap" align="center">
        <Select
          mode="multiple"
          placeholder="Status"
          value={filters.status || []}
          onChange={(v) =>
            setFilters({ status: v.length ? v : undefined, page: undefined })
          }
          options={STATUS_OPTIONS}
          style={{ minWidth: 160 }}
          maxTagCount="responsive"
          allowClear
        />
        <Select
          mode="multiple"
          placeholder="Document type"
          value={filters.documentType || []}
          onChange={(v) =>
            setFilters({
              documentType: v.length ? v : undefined,
              page: undefined,
            })
          }
          options={DOCUMENT_TYPE_OPTIONS}
          style={{ minWidth: 180 }}
          maxTagCount="responsive"
          allowClear
        />
        <Select
          mode="multiple"
          placeholder="Product"
          value={filters.products || []}
          onChange={(v) =>
            setFilters({ products: v.length ? v : undefined, page: undefined })
          }
          options={PRODUCT_OPTIONS}
          style={{ minWidth: 140 }}
          maxTagCount="responsive"
          allowClear
        />
        <Select
          mode="multiple"
          placeholder="Country"
          value={filters.country || []}
          onChange={(v) =>
            setFilters({ country: v.length ? v : undefined, page: undefined })
          }
          options={countryOptions}
          style={{ minWidth: 200 }}
          maxTagCount="responsive"
          allowClear
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => {
            const search = input.toLowerCase()
            return (
              String(option?.label ?? '').toLowerCase().includes(search) ||
              String(option?.value ?? '').toLowerCase().includes(search)
            )
          }}
        />
        <Select
          mode="multiple"
          placeholder="Manager"
          value={filters.manager || []}
          onChange={(v) =>
            setFilters({ manager: v.length ? v : undefined, page: undefined })
          }
          options={managerOptions}
          style={{ minWidth: 200 }}
          maxTagCount="responsive"
          allowClear
          showSearch
          optionFilterProp="label"
        />

        <div
          style={{
            width: 1,
            height: 28,
            background: 'var(--crm-border, #e2e5ec)',
          }}
        />

        <BoolFilter
          label="Deposit"
          value={filters.hasDeposit}
          onChange={(v) => setFilters({ hasDeposit: v, page: undefined })}
        />
        <BoolFilter
          label="Duplicate"
          value={filters.isDuplicate}
          onChange={(v) => setFilters({ isDuplicate: v, page: undefined })}
        />
        <BoolFilter
          label="Premium"
          value={filters.isPremium}
          onChange={(v) => setFilters({ isPremium: v, page: undefined })}
        />
      </Flex>

      {activeTags.length > 0 && (
        <>
          <div
            style={{
              height: 1,
              background: 'var(--crm-border, #e2e5ec)',
              margin: '10px 0 8px',
            }}
          />
          <Flex gap={6} wrap="wrap" align="center">
            <Text
              type="secondary"
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                whiteSpace: 'nowrap',
              }}
            >
              Active:
            </Text>
            {activeTags.map((t) => (
              <Tag
                key={t.key}
                closable
                onClose={t.clear}
                style={{ fontSize: 11, margin: 0 }}
                color="blue"
              >
                {t.label}
              </Tag>
            ))}
            <Button
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => {
                resetFilters()
                setInputRequestId('')
                setInputClientId('')
                setInputEmail('')
                setInputFullName('')
              }}
              style={{ color: '#ef4444', fontSize: 11 }}
            >
              Reset all
            </Button>
          </Flex>
        </>
      )}
    </div>
  )
}
